/**
 * Provider that owns the command-palette open state and the global Cmd/Ctrl-K
 * keyboard shortcut (issue #1440). Mounted once inside each authenticated app
 * shell so the palette works from both the admin and CRM shells.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CommandPalette } from "./CommandPalette";
import { GlobalSearchContext } from "./globalSearchContext";

export function GlobalSearchProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Cmd/Ctrl-K toggles the palette from anywhere in the shell.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
      <CommandPalette open={isOpen} onClose={close} />
    </GlobalSearchContext.Provider>
  );
}
